import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../api/errors';

export interface QueryState<T> {
  data: T | null;
  error: ApiError | null;
  loading: boolean;
  reload: () => Promise<void>;
  setData: (value: T | null) => void;
}

function toApiError(err: unknown): ApiError {
  return err instanceof ApiError ? err : new ApiError({ status: 0, code: 'INTERNAL_ERROR', message: String((err as Error)?.message ?? err) });
}

/**
 * Loads `fn` on mount and whenever `key` changes. `pollMs` re-runs it on an
 * interval while the page is visible (the verify and test-lead screens wait
 * for things that happen elsewhere). A stale response never overwrites a
 * newer one.
 */
export function useQuery<T>(fn: () => Promise<T>, key: unknown[], options: { enabled?: boolean; pollMs?: number } = {}): QueryState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState<boolean>(options.enabled !== false);
  const seq = useRef(0);
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const enabled = options.enabled !== false;

  const run = useCallback(async (quiet = false) => {
    const id = ++seq.current;
    if (!quiet) setLoading(true);
    try {
      const result = await fnRef.current();
      if (id === seq.current) {
        setData(result);
        setError(null);
      }
    } catch (err) {
      if (id === seq.current) setError(toApiError(err));
    } finally {
      if (id === seq.current) setLoading(false);
    }
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const depKey = JSON.stringify(key);
  useEffect(() => {
    if (!enabled) return;
    void run();
  }, [depKey, enabled, run]);

  useEffect(() => {
    if (!enabled || !options.pollMs) return;
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void run(true);
    }, options.pollMs);
    return () => window.clearInterval(timer);
  }, [enabled, options.pollMs, run, depKey]);

  return { data, error, loading, reload: () => run(true), setData };
}

export interface MutationState<A extends unknown[], R> {
  run: (...args: A) => Promise<R | undefined>;
  pending: boolean;
  error: ApiError | null;
  reset: () => void;
}

/** A write with pending/error state. Resolves `undefined` on failure (the error is in state). */
export function useMutation<A extends unknown[], R>(fn: (...args: A) => Promise<R>): MutationState<A, R> {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const run = useCallback(async (...args: A) => {
    setPending(true);
    setError(null);
    try {
      return await fnRef.current(...args);
    } catch (err) {
      setError(toApiError(err));
      return undefined;
    } finally {
      setPending(false);
    }
  }, []);
  return { run, pending, error, reset: () => setError(null) };
}

/** A value persisted per browser (UI conveniences only — never business state). */
export function useLocalState<T>(key: string, initial: T): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = window.localStorage.getItem(key);
      return raw === null ? initial : (JSON.parse(raw) as T);
    } catch {
      return initial;
    }
  });
  const set = useCallback(
    (next: T) => {
      setValue(next);
      try {
        window.localStorage.setItem(key, JSON.stringify(next));
      } catch {
        /* ignore */
      }
    },
    [key],
  );
  return [value, set];
}

/** The `.cards` breakpoint in styles.css — keep the two in step. */
export const PHONE_QUERY = '(max-width: 720px)';

function phoneNow(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(PHONE_QUERY).matches;
}

/**
 * True below the `.cards` breakpoint. Screens whose rows carry actions
 * (Sites & keys, "Where your key is used") render EITHER the table or the
 * cards rather than both hidden by CSS, so each Block / Rotate / Copy button
 * exists once in the accessibility tree. Without `matchMedia` (jsdom) it is
 * false: the desktop table.
 */
export function usePhone(): boolean {
  const [phone, setPhone] = useState(phoneNow);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(PHONE_QUERY);
    const on = () => setPhone(mq.matches);
    on();
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return phone;
}
